import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PageChromeProvider } from './PageChrome';
import { CampaignSidebar } from './CampaignSidebar';
import { CampaignTopBar } from './CampaignTopBar';

import { WizardProvider } from './store/wizardStore';

import { NewCampaignWizard } from './pages/NewCampaignWizard';
import { useCampaignFonts } from '../campaign-os';
import '../campaign-os/campaign-os.css';

/**
 * CampaignOS full-takeover shell. Ported from
 * D:\campaigning-system\src\layout\AppLayout.tsx.
 *
 * This renders OUTSIDE VMS's main <Layout> (see Routes.jsx), so entering the
 * Campaigning Tool replaces VMS's sidebar/header entirely with the CampaignOS
 * mini sidebar + top bar. The whole subtree is wrapped in `.campaign-os` (ivory
 * canvas, Hanken font, frosted glass — scoped so nothing leaks) and loads the
 * web fonts route-scoped via useCampaignFonts. Child routes render in <Outlet>.
 */
export function CampaignShell() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useCampaignFonts();

  return (
    // Self-contained scroll (mirrors VMS's Layout exactly): fixed-height flex shell,
    // TopBar pinned at the top of the content column, and <main> as the scroll area via
    // `flex-1 min-h-0 overflow-y-auto`. The `min-h-0` is essential — without it a flex
    // child won't shrink below its content, so overflow-y-auto never engages and the
    // column grows past the viewport, hiding content below the fold.
    <div className="campaign-os flex h-screen overflow-hidden">
        <WizardProvider>
          <PageChromeProvider>
            <CampaignSidebar mobileOpen={navOpen} onNavigate={() => setNavOpen(false)} />
            <div className="flex min-w-0 flex-1 flex-col lg:pl-[248px]">
              <CampaignTopBar onMenu={() => setNavOpen(true)} />
              <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 xl:px-8 xl:py-7">
                <div key={location.pathname} className="mx-auto max-w-[1920px] animate-fade-in">
                  <Outlet />
                </div>
              </main>
            </div>
            {/* Mounted once at the shell level so the wizard is reachable from every screen
                (Dashboard "New Campaign" + Campaigns "New Campaign"). */}
            <NewCampaignWizard />
          </PageChromeProvider>
        </WizardProvider>
    </div>
  );
}

export default CampaignShell;
